import React, { useState, useEffect, useCallback } from "react";
import {
    PlayIcon,
    PauseIcon,
    BackwardIcon,
    ForwardIcon,
    MinusIcon,
    PlusIcon,
    SpeakerWaveIcon,
    SpeakerXMarkIcon,
} from "@heroicons/react/24/solid";
import { unlockAudio } from "../utils/sound";

interface TimerControlsProps {
    isRunning: boolean;
    onStartPause: () => void;
    onPrevLevel: () => void;
    onNextLevel: () => void;
    onAddMinute: () => void;
    onSubtractMinute: () => void;
}

export default function TimerControls({
                                          isRunning,
                                          onStartPause,
                                          onPrevLevel,
                                          onNextLevel,
                                          onAddMinute,
                                          onSubtractMinute,
                                      }: TimerControlsProps) {
    const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
        const stored = localStorage.getItem("soundEnabled");
        return stored === null ? true : stored === "true";
    });

    // speichere den Sound-Status persistent
    useEffect(() => {
        localStorage.setItem("soundEnabled", String(soundEnabled));
    }, [soundEnabled]);

    // Beim Start/Pause-Klick auch Audio aktivieren (Browser-Sperre umgehen)
    const handleStartPause = useCallback(async () => {
        await unlockAudio();
        onStartPause();
    }, [onStartPause]);

    const toggleSound = useCallback(async () => {
        await unlockAudio(); // ebenfalls AudioContext entsperren
        setSoundEnabled((prev) => !prev);
    }, []);

    return (
        <div className="absolute top-4 right-4 flex items-center gap-3 md:gap-4 z-10">
            {/* -1 Minute */}
            <button
                onClick={onSubtractMinute}
                title="Eine Minute abziehen"
                aria-label="Eine Minute abziehen"
            >
                <MinusIcon className="w-5 h-5 text-white" />
            </button>

            {/* Vorheriges Level */}
            <button
                onClick={onPrevLevel}
                title="Vorheriges Level"
                aria-label="Vorheriges Level"
            >
                <BackwardIcon className="w-8 h-8 text-white" />
            </button>

            {/* Start/Pause */}
            <button
                onClick={handleStartPause}
                title="Start/Pause"
                aria-label="Start oder Pause"
            >
                {isRunning ? (
                    <PauseIcon className="w-8 h-8 text-white" />
                ) : (
                    <PlayIcon className="w-8 h-8 text-white" />
                )}
            </button>

            {/* Nächstes Level */}
            <button
                onClick={onNextLevel}
                title="Nächstes Level"
                aria-label="Nächstes Level"
            >
                <ForwardIcon className="w-8 h-8 text-white" />
            </button>

            {/* +1 Minute */}
            <button
                onClick={onAddMinute}
                title="Eine Minute hinzufügen"
                aria-label="Eine Minute hinzufügen"
            >
                <PlusIcon className="w-5 h-5 text-white" />
            </button>

            {/* 🔇 Sound Toggle */}
            <button
                onClick={toggleSound}
                title={soundEnabled ? "Sound ausschalten" : "Sound einschalten"}
                aria-label={soundEnabled ? "Sound ausschalten" : "Sound einschalten"}
            >
                {soundEnabled ? (
                    <SpeakerWaveIcon className="w-6 h-6 text-white" />
                ) : (
                    <SpeakerXMarkIcon className="w-6 h-6 text-white opacity-70" />
                )}
            </button>
        </div>
    );
}
